// 右上「角色符號 → 角色百搭」連消收集面板 — 照 Unity LandscapeUI.prefab 的 WildInfoGroup 精確還原。
// 規則（zh-Hant 字表第一條）：「角色符號在每 2 次消除後將轉化為角色百搭符號」。
//
// 版面（Unity 設計座標，中心原點、y 向上）：
//   WildInfoGroup aPos (570.47, 413.50)＝遊戲 Logo 左下方；三行 WildInfo_1/2/3 相對 y = +72.7 / −8.5 / −89.2。
//   每行：木橫條底（pic_wood 266×84，自帶 2 個圓孔）＋ GoldBackground（pic_woodGlod 同尺寸金光版，
//   轉化時 0.6s 淡入——對照 _h2BackgroundImage.DOFade(1,0.6)）＋ SymbolImage（H2/H3/H4 靜態頭像，
//   相對行 (−49.9, 5.7)、scale 0.6）＋ H{n}_ChangeWild spine（同位置，轉化時播 change_1x1 蓋住靜態頭像，
//   結尾定格為百搭造型）＋ 兩個收集點 Point（相對行 (41, 10)/(94, 10)、50×50，各掛一份 ani_collectWW，
//   點亮時 α→1 播 `animation` 一次）。
//
// 行為（對照 GamePlayUI.ShowWildInfo(index)/HideWildInfo 與 SlotStateMachine.DestroySymbol_Enter）：
//   每次有贏分的消除步驟 → combo+1 → 點亮 Point[combo-1]；index=1/3/5（第 2/4/6 孔）時該行角色
//   「轉化為百搭」（change spine＋金底淡入）。每次開轉（reelSpinStarted）重置：change spine 隱藏、
//   金底 0.6s 淡出、收集點 0.2s 淡出。主遊戲與免費遊戲行為一致、面板恆顯示（Unity 從不 SetActive 此群組）。
//   ⚠️ [刻意偏離 Unity 時機] 進度點亮改綁 tumbleStepResolved（消除下墜完成後）而非 stepWinPresented
//   （消除動畫之前）——依使用者要求「等消除完成後再更新進度」，見 constructor 內註解。
//
// 骨骼載入失敗時 fallback：點亮=WW 符號小圖、轉化=頭像換 W2/W3/W4 貼圖（先前版本行為）。

import { Container, Sprite } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import {
  unityAnchorLandscape,
  unityAnchorPortrait,
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../../framework/render/design'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import { getUiTexture } from '../../assets/UiTextures'
import { getSymbolTexture, getWildVariantTexture } from '../../assets/SymbolTextures'
import { createSymbolSpine } from '../../assets/SymbolSpine'
import { createEffectSpine, EFFECT_ANIM, hasAnimation } from '../../assets/EffectSpines'
import { killTweensOf, tweenTo } from '../tween'

/**
 * Unity WildInfoGroup 版面常數（設計座標，取自 LandscapeUI.prefab 序列化值）。
 * [修正 2026-07-28] GROUP_AY 曾被改為 373.3（依「實機截圖三行整體較低」推定），已回復 prefab 值 413.5：
 * 重新核對確認此節點在 Scene 的 1751 筆 prefab 覆寫中完全未被提及、PoriraitUI 變體亦無覆寫、
 * 節點只有 RectTransform（無 VerticalLayoutGroup/ContentSizeFitter 會在執行期重算版面）、
 * GamePlayUI 也只改圖片/骨骼不搬動 transform → prefab 值即執行期值，先前的截圖推定為誤判。
 */
const GROUP_AX = 570.47
const GROUP_AY = 413.5
/** 直版覆寫（PoriraitUI WildInfoGroup aPos (−397.7, 673.3)）。 */
const GROUP_P_AX = -397.7
const GROUP_P_AY = 673.3
/** 三行相對群組中心的 y（Unity y 向上；PixiJS 取負）。 */
const ROW_DYS: readonly number[] = [72.7, -8.5, -89.2]
/** 各行對應的高分符號 id（H2/H3/H4，見 config/symbols）。 */
const ROW_SYMBOL_IDS: readonly number[] = [2, 3, 4]
/** 木橫條底圖（pic_wood / pic_woodGlod）設計寬（設計尺寸 266×84，等比縮放故只需寬）。 */
const BAR_W = 266
/**
 * 角色頭像相對行中心位置與顯示高。
 * Unity SymbolImage：H{n}_1x1 貼圖（原生 214×192）× localScale 0.6、PreserveAspect=0、aPos (−49.9, +5.7)，
 * 故顯示高 = 192 × 0.6 = 115.2 設計 px（layoutSymbol 以「完整未 trim 幀」texture.height=192 換算即得 scale 0.6）。
 * 權威值取自 LandscapeUI.prefab（三行 SymbolImage 逐欄位相同；Scene 與 PoriraitUI 皆未覆寫其尺寸／縮放）。
 * ⚠️ 頭像高（115.2）＞木條高（84）＝Unity 原貌：頭部微探出木條頂約 8 設計 px、非破版。
 * [修正 2026-07-29] 舊值 80（≈scale 0.417）比 Unity 小約 30%，與本檔檔頭註解自述的「scale 0.6」不符，已校正。
 */
const SYMBOL_DX = -49.9
const SYMBOL_DY = 5.7
const SYMBOL_H = 115.2
/** change spine 的 Unity 節點 scale（H2_ChangeWild m_LocalScale=0.6）。 */
const CHANGE_SPINE_SCALE = 0.6
/** 兩個收集點相對行中心位置與尺寸（Unity Point1/Point2）。 */
const POINT_DXS: readonly number[] = [41, 94]
const POINT_DY = 10
const POINT_SIZE = 50
/** 點亮彈出時長（秒，fallback 用）。 */
const POP_DURATION = 0.24
/** 金底淡入/淡出時長（Unity DOFade 0.6s）與收集點淡出（0.2s）。 */
const GOLD_FADE_DURATION = 0.6
const POINT_FADE_OUT = 0.2

interface WildInfoRow {
  readonly bar: Sprite
  readonly goldBar: Sprite
  readonly symbol: Sprite
  readonly changeSpine: Spine | null
  readonly points: readonly Sprite[]
  readonly pointSpines: readonly (Spine | null)[]
}

export class WildInfoView extends Container implements Resizable {
  private readonly rows: WildInfoRow[]
  /** 本次旋轉的連消計數（每個有贏分的消除步驟 +1），對應 Unity _gameData.ComboCount。 */
  private combo = 0
  private scaleFactor = 1

  constructor(bus: EventBus<GameEventMap>) {
    super()
    this.eventMode = 'none'

    this.rows = ROW_SYMBOL_IDS.map((symbolId) => {
      const bar = new Sprite(getUiTexture('woodBeam'))
      bar.anchor.set(0.5)
      const goldBar = new Sprite(getUiTexture('woodBeamGold'))
      goldBar.anchor.set(0.5)
      goldBar.alpha = 0
      const symbol = new Sprite(getSymbolTexture(symbolId, 1))
      symbol.anchor.set(0.5)
      const changeSpine = createSymbolSpine(symbolId)
      if (changeSpine !== null) changeSpine.visible = false
      const points = POINT_DXS.map(() => {
        const point = new Sprite(getSymbolTexture(0, 1))
        point.anchor.set(0.5)
        point.visible = false
        return point
      })
      const pointSpines = POINT_DXS.map(() => {
        const spine = createEffectSpine('collectWW')
        if (spine !== null) spine.alpha = 0
        return spine
      })
      this.addChild(bar, goldBar, ...points, symbol)
      if (changeSpine !== null) this.addChild(changeSpine)
      for (const spine of pointSpines) if (spine !== null) this.addChild(spine)
      return { bar, goldBar, symbol, changeSpine, points, pointSpines }
    })

    // 每次有贏分的消除步驟 → combo+1 → 點亮下一孔。
    // [刻意偏離 Unity 時機] Unity 在 DestroySymbol_Enter（消除動畫「開始」時）ShowWildInfo(ComboCount)；
    // 依使用者要求改在「消除完成後」才點亮，故監聽 tumbleStepResolved（存活符號下墜到位、playCollapse
    // 之後 emit），而非 stepWinPresented（消除動畫之前）——避免中獎符號還沒消失、進度孔就先亮起的突兀感。
    // 兩事件皆為「每個有贏分的消除步驟各 emit 一次」，數量／順序一致，改綁只延後點亮時機、不影響 combo 計數。
    bus.on('tumbleStepResolved', () => this.advance())
    // 每次開轉重置（對應 Rotate_Enter 的 HideWildInfo；主遊戲與免費遊戲皆同）。
    bus.on('reelSpinStarted', () => this.reset())
  }

  /** 點亮下一個收集孔；每滿 2 孔該行角色頭像轉為百搭造型（對照 ShowWildInfo 的 index=1/3/5 分支）。 */
  private advance(): void {
    this.combo += 1
    const index = this.combo - 1
    const row = this.rows[Math.floor(index / 2)]
    if (row === undefined) return

    const pointSpine = row.pointSpines[index % 2]
    if (pointSpine !== null && pointSpine !== undefined) {
      // Unity：DOFade(1,0) ＋ 播 ani_collectWW `animation` 一次（結尾定格＝點亮狀態）。
      killTweensOf(pointSpine)
      pointSpine.alpha = 1
      pointSpine.state.setAnimation(0, EFFECT_ANIM.wildInfoPoint, false)
    } else {
      const point = row.points[index % 2]
      if (point !== undefined) {
        point.visible = true
        killTweensOf(point.scale)
        const target = this.pointScale(point)
        point.scale.set(target * 1.7)
        void tweenTo(point.scale, { x: target, y: target, duration: POP_DURATION, ease: 'back.out(2.2)' })
      }
    }

    // 每收滿 2 孔（combo=2/4/6）：該行角色符號「轉化為百搭」＋金底淡入。
    if (this.combo % 2 === 0) {
      killTweensOf(row.goldBar)
      void tweenTo(row.goldBar, { alpha: 1, duration: GOLD_FADE_DURATION })

      if (row.changeSpine !== null && hasAnimation(row.changeSpine, EFFECT_ANIM.wildInfoChange)) {
        row.changeSpine.visible = true
        row.changeSpine.state.setAnimation(0, EFFECT_ANIM.wildInfoChange, false)
      } else {
        const rowIndex = Math.floor(index / 2)
        const wildTexture = getWildVariantTexture(ROW_SYMBOL_IDS[rowIndex] ?? 0, 1)
        if (wildTexture !== null) {
          row.symbol.texture = wildTexture
          this.layoutSymbol(row.symbol)
          killTweensOf(row.symbol.scale)
          const target = row.symbol.scale.x
          row.symbol.scale.set(target * 1.25)
          void tweenTo(row.symbol.scale, { x: target, y: target, duration: POP_DURATION, ease: 'back.out(2.2)' })
        }
      }
    }
  }

  /** 重置連消進度（對照 HideWildInfo：change spine 隱藏、金底 0.6s 淡出、收集點 0.2s 淡出）。 */
  private reset(): void {
    this.combo = 0
    this.rows.forEach((row, rowIndex) => {
      row.symbol.texture = getSymbolTexture(ROW_SYMBOL_IDS[rowIndex] ?? 0, 1)
      this.layoutSymbol(row.symbol)
      if (row.changeSpine !== null) row.changeSpine.visible = false
      killTweensOf(row.goldBar)
      void tweenTo(row.goldBar, { alpha: 0, duration: GOLD_FADE_DURATION })
      for (const point of row.points) {
        killTweensOf(point.scale)
        point.visible = false
      }
      for (const spine of row.pointSpines) {
        if (spine !== null && spine.alpha > 0) {
          killTweensOf(spine)
          void tweenTo(spine, { alpha: 0, duration: POINT_FADE_OUT })
        }
      }
    })
  }

  /** 收集點顯示縮放（50×50 設計尺寸相對貼圖原生尺寸）。 */
  private pointScale(point: Sprite): number {
    return (POINT_SIZE * this.scaleFactor) / Math.max(1, point.texture.width)
  }

  /** 頭像以「高度＝SYMBOL_H 設計 px」等比縮放。 */
  private layoutSymbol(symbol: Sprite): void {
    symbol.scale.set((SYMBOL_H * this.scaleFactor) / Math.max(1, symbol.texture.height))
  }

  resize(width: number, height: number, orientation: Orientation): void {
    const isPortrait = orientation === 'portrait'
    // 直版照 PoriraitUI 覆寫：WildInfoGroup aPos (−397.7, 673.3)（scale 未覆寫＝1）。
    const s = isPortrait ? unityScalePortrait(width, height) : unityScaleLandscape(width, height)
    this.scaleFactor = s
    const center = isPortrait
      ? unityAnchorPortrait(GROUP_P_AX, GROUP_P_AY, width, height)
      : unityAnchorLandscape(GROUP_AX, GROUP_AY, width, height)

    this.rows.forEach((row, rowIndex) => {
      const rowY = center.y - (ROW_DYS[rowIndex] ?? 0) * s
      const barScale = (BAR_W * s) / row.bar.texture.width
      row.bar.position.set(center.x, rowY)
      row.bar.scale.set(barScale)
      row.goldBar.position.set(center.x, rowY)
      row.goldBar.scale.set((BAR_W * s) / row.goldBar.texture.width)

      row.symbol.position.set(center.x + SYMBOL_DX * s, rowY - SYMBOL_DY * s)
      this.layoutSymbol(row.symbol)
      if (row.changeSpine !== null) {
        row.changeSpine.position.set(center.x + SYMBOL_DX * s, rowY - SYMBOL_DY * s)
        row.changeSpine.scale.set(CHANGE_SPINE_SCALE * s)
      }

      row.points.forEach((point, pointIndex) => {
        point.position.set(center.x + (POINT_DXS[pointIndex] ?? 0) * s, rowY - POINT_DY * s)
        point.scale.set(this.pointScale(point))
      })
      row.pointSpines.forEach((spine, pointIndex) => {
        if (spine !== null) {
          spine.position.set(center.x + (POINT_DXS[pointIndex] ?? 0) * s, rowY - POINT_DY * s)
          spine.scale.set(s)
        }
      })
    })
  }
}
